/**
 * Single source of truth for vignette product duration logic.
 *
 * Backend product shape (from /products/categories or /products/parents):
 *   {
 *     duration: 1,           // numeric count
 *     duration_type: "months" // "days" | "weeks" | "months" | "years" (also singular variants)
 *     ...
 *   }
 *
 * Older parts of the codebase only had `duration` (interpreted as days). This
 * module bridges the two systems:
 *   - `getProductDurationDays(p)`    → canonical days for sorting, icon
 *                                     selection, comparisons.
 *   - `getProductDurationDescriptor` → `{ count, unit, days }` for display.
 *   - `formatProductDurationLabel`   → user-facing string like "7 days",
 *                                     "1 month", "6 months", "1 year". Title
 *                                     fallback is used so localized backend
 *                                     titles stay consistent with API.
 *   - `getProductDurationKey`        → stable string identifier (canonical
 *                                     days) used as map key in card metadata
 *                                     and sort buckets.
 */

const DAY_UNIT_FACTORS = {
    day: 1,
    days: 1,
    week: 7,
    weeks: 7,
    month: 30,
    months: 30,
    year: 365,
    years: 365,
};

const CANONICAL_UNITS = {
    day: 'days', days: 'days',
    week: 'weeks', weeks: 'weeks',
    month: 'months', months: 'months',
    year: 'years', years: 'years',
};

function normalizeUnit(rawUnit) {
    const s = String(rawUnit ?? '').trim().toLowerCase();
    return CANONICAL_UNITS[s] || '';
}

function singularize(unit) {
    if (!unit) return '';
    return unit.endsWith('s') ? unit.slice(0, -1) : unit;
}

/**
 * Numeric count (e.g. 1, 6, 7). 0 means "unknown / invalid".
 */
export function getProductDurationCount(product) {
    const raw = Number(product?.duration);
    return Number.isFinite(raw) && raw > 0 ? raw : 0;
}

/**
 * Canonical plural unit string ('days' | 'weeks' | 'months' | 'years').
 * Falls back to 'days' when backend omits `duration_type`, matching legacy
 * behaviour.
 */
export function getProductDurationUnit(product) {
    return normalizeUnit(product?.duration_type) || 'days';
}

/**
 * Canonical days for the product. Useful for icon mapping, sorting and the
 * `selected_duration` field in formData (which historically held days).
 *
 *   { count: 1, unit: 'months' } → 30
 *   { count: 6, unit: 'months' } → 180
 *   { count: 1, unit: 'years' }  → 365
 *   { count: 7, unit: 'days' }   → 7
 */
export function getProductDurationDays(product) {
    const count = getProductDurationCount(product);
    if (!count) return 0;
    const unit = getProductDurationUnit(product);
    const factor = DAY_UNIT_FACTORS[unit] ?? DAY_UNIT_FACTORS.days;
    return count * factor;
}

/**
 * Display-only overrides for the *shown* count. Some backend records use an
 * off-by-one convention (e.g. a vignette marketed as "7 days" is stored as
 * `duration: 6, duration_type: "days"`). We must NOT change the canonical day
 * math (sorting, buckets, `selected_duration` sent to the API), only what the
 * user sees. Keyed by canonical unit so "6 months" (half-year) stays untouched.
 *
 *   { 6 days } → shown as "7 days"
 */
const DISPLAY_COUNT_OVERRIDES = {
    days: { 6: 7 },
};

/**
 * Count to show in user-facing labels. Equals the raw count unless a
 * unit-specific display override applies (see DISPLAY_COUNT_OVERRIDES).
 */
export function getDisplayDurationCount(product) {
    const count = getProductDurationCount(product);
    if (!count) return 0;
    const unit = getProductDurationUnit(product);
    return DISPLAY_COUNT_OVERRIDES[unit]?.[count] ?? count;
}

/**
 * Bundled descriptor — convenient for places that need multiple parts at once.
 */
export function getProductDurationDescriptor(product) {
    return {
        count: getProductDurationCount(product),
        unit: getProductDurationUnit(product),
        days: getProductDurationDays(product),
    };
}

/**
 * Sort comparator: shorter durations come first.
 */
export function compareProductDurations(a, b) {
    return getProductDurationDays(a) - getProductDurationDays(b);
}

/**
 * Stable string id for the product's duration. Returns canonical days as
 * string ('30', '180', '365', '6', '7', …) — i.e. exactly what the API
 * encodes. Use this when you need an exact identifier (e.g. sort key).
 */
export function getProductDurationKey(product) {
    const days = getProductDurationDays(product);
    return days ? String(days) : '';
}

/**
 * Coarse "marketing bucket" for the product's duration. Several backend
 * conventions exist for what "7-day" or "monthly" actually means in raw
 * count (e.g. some APIs return `duration: 6, duration_type: "days"` for a
 * vignette marketed as "7 days"), so card metadata (`CARD_META` in
 * `PricingSection.vue`) keys on these stable buckets:
 *
 *   ≥ 365  → '365' (annual)
 *   ≥ 90   → '180' (half-year)
 *   ≥ 14   → '30'  (monthly)
 *   ≥ 8    → '10'  (extended short)
 *   ≥ 2    → '7'   (short / transit)
 *   = 1    → '1'   (single day)
 *
 * Use `getProductDurationKey` if you need the exact day count; use this
 * helper when you're matching against a fixed style/text mapping.
 */
export function getProductDurationBucket(product) {
    const days = getProductDurationDays(product);
    if (!days) return '';
    if (days >= 365) return '365';
    if (days >= 90)  return '180';
    if (days >= 14)  return '30';
    if (days >= 8)   return '10';
    if (days >= 2)   return '7';
    return '1';
}

/**
 * User-facing label like "7 days" or "1 month". When the backend `title`
 * contains a parenthetical fragment (e.g. "Car or camper < 3500kg (1 month)")
 * that fragment wins — the API already speaks the user's locale and we
 * mustn't double-translate.
 *
 * @param {object} product
 * @param {object} [options]
 * @param {boolean} [options.preferDescriptor=false]  Skip the title fragment
 *        and always format from `(count, unit)`.
 * @param {(word: string) => string} [options.translateUnit]  Optional resolver
 *        that maps the canonical unit word ('day' | 'days' | 'week' | 'weeks' |
 *        'month' | 'months' | 'year' | 'years') to a localized string. Applied
 *        only to the `(count, unit)` path; the title fragment is left untouched
 *        because the backend already speaks the user's locale.
 */
export function formatProductDurationLabel(product, options = {}) {
    if (!product || typeof product !== 'object') return '';
    const { preferDescriptor = false, translateUnit } = options;

    if (!preferDescriptor) {
        const fromTitle = extractParentheticalLabel(
            product?.translated?.title ?? product?.title ?? product?.name ?? ''
        );
        if (fromTitle) return applyDisplayCountOverride(product, fromTitle);
    }

    const { count, unit } = getProductDurationDescriptor(product);
    if (!count) return '';

    const displayCount = getDisplayDurationCount(product);
    const rawWord = displayCount === 1 ? singularize(unit) : unit;
    const word = typeof translateUnit === 'function'
        ? (translateUnit(rawWord) || rawWord)
        : rawWord;
    return `${displayCount} ${word}`.trim();
}

/**
 * Swaps the raw count for its display override inside an already-formatted,
 * possibly localized label (e.g. "6 days" → "7 days", "6 dní" → "7 dní").
 * Replaces only the first standalone occurrence of the raw number, so words
 * and other digits are left intact. No-op when there's no override.
 */
function applyDisplayCountOverride(product, label) {
    const count = getProductDurationCount(product);
    const displayCount = getDisplayDurationCount(product);
    if (!count || count === displayCount) return label;
    return String(label).replace(new RegExp(`\\b${count}\\b`), String(displayCount));
}

/**
 * Pulls "1 month" out of "Car or camper < 3500kg (1 month)".
 * Returns '' when no parenthetical fragment is present.
 */
function extractParentheticalLabel(title) {
    const match = String(title ?? '').match(/\(([^)]+)\)/);
    if (!match) return '';
    const candidate = match[1].trim();
    if (!/\d/.test(candidate)) return '';
    return candidate;
}

/**
 * Pick a single representative product per duration bucket from a list.
 * First product per bucket wins. Result preserves insertion order.
 */
export function uniqueByDuration(products) {
    const seen = new Set();
    const out = [];
    (Array.isArray(products) ? products : []).forEach((p) => {
        const key = getProductDurationKey(p);
        if (!key || seen.has(key)) return;
        seen.add(key);
        out.push(p);
    });
    return out;
}

/**
 * Bucket constants and helpers that callers may want for grouping logic
 * (e.g. PricingSection cards).
 */
export const DURATION_BUCKETS = {
    DAY_1: '1',
    DAY_7: '7',
    DAY_10: '10',
    DAY_30: '30',     // also '1 month'
    DAY_180: '180',   // also '6 months'
    DAY_365: '365',   // also '1 year'
};
